import { useEffect, useState } from "react";
import api from "../api";
import { motion } from "framer-motion";
import {
    User as UserIcon,
    Mail,
    Phone,
    ShieldCheck,
    LogOut,
    Loader2,
    AtSign,
    Camera,
} from "lucide-react";

interface User {
    id: number;
    name: string;
    username: string;
    email: string;
    phone: string;
    role: string;
}

export default function Profile() {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api
            .get("/users/me")
            .then((res) => {
                setUser(res.data.data);
            })
            .catch((err) => console.error("Profile fetch failed", err))
            .finally(() => setLoading(false));
    }, []);

    const handleLogout = () => {
        localStorage.removeItem("token");
        window.location.href = "/";
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#020617] flex items-center justify-center">
                <Loader2 className="text-[#8b5cf6] animate-spin" size={40} />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#020617] relative overflow-hidden py-10 px-4">
            {/* Background Accents */}
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#8b5cf6]/10 blur-[120px] rounded-full pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-600/5 blur-[120px] rounded-full pointer-events-none" />

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-2xl mx-auto relative z-10"
            >
                {/* Profile Card */}
                <div className="bg-slate-900/40 backdrop-blur-2xl border border-slate-800 rounded-[2.5rem] shadow-3xl overflow-hidden">
                    {/* Cover Area & Logo */}
                    <div className="h-32 bg-gradient-to-r from-[#8b5cf6] to-[#6d28d9] relative">
                        <div className="absolute -bottom-12 left-8 p-1 bg-slate-900 rounded-2xl border-4 border-slate-900">
                            <div className="w-24 h-24 bg-slate-800 rounded-xl flex items-center justify-center overflow-hidden">
                                {/* Fallback to User Icon if no avatar, but we'll show your brand logo here too */}
                                <img
                                    src="/logo.png"
                                    alt="Brand"
                                    className="w-12 h-12 object-contain opacity-50 absolute"
                                />
                                <UserIcon className="text-slate-400 relative z-10" size={40} />
                            </div>
                            <button className="absolute bottom-1 right-1 p-1.5 bg-[#8b5cf6] rounded-lg border-2 border-slate-900 text-white hover:bg-[#7c3aed] transition-colors">
                                <Camera size={14} />
                            </button>
                        </div>
                    </div>

                    <div className="pt-16 pb-8 px-8">
                        {/* Header Info */}
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                                <h1 className="text-3xl font-bold text-white tracking-tight">
                                    {user?.name}
                                </h1>
                                <div className="flex items-center gap-2 text-slate-400 mt-1">
                                    <AtSign size={14} />
                                    <span className="text-sm font-medium">{user?.username}</span>
                                    <span className="px-2 py-0.5 rounded-full bg-[#8b5cf6]/10 text-[#8b5cf6] text-[10px] font-bold uppercase tracking-wider border border-[#8b5cf6]/20">
                                        {user?.role}
                                    </span>
                                </div>
                            </div>

                            <button
                                onClick={handleLogout}
                                className="flex items-center justify-center gap-2 px-6 py-3 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 rounded-xl font-bold text-sm transition-all active:scale-95"
                            >
                                <LogOut size={18} />
                                Logout
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10">
                            {/* Info Tiles */}
                            <InfoTile
                                icon={<Mail className="text-[#8b5cf6]" size={20} />}
                                label="Email Address"
                                value={user?.email || "N/A"}
                            />
                            <InfoTile
                                icon={<Phone className="text-[#8b5cf6]" size={20} />}
                                label="Phone Number"
                                value={user?.phone || "N/A"}
                            />
                            <InfoTile
                                icon={<ShieldCheck className="text-[#8b5cf6]" size={20} />}
                                label="Account Status"
                                value="Verified Member"
                            />
                            <InfoTile
                                icon={<UserIcon className="text-[#8b5cf6]" size={20} />}
                                label="User ID"
                                value={`#${user?.id?.toString().padStart(4, "0")}`}
                            />
                        </div>

                        {/* Bottom Brand Section */}
                        <div className="mt-10 pt-8 border-t border-slate-800 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <img
                                    src="/logo.png"
                                    alt="Logo"
                                    className="w-8 h-8 object-contain"
                                />
                                <span className="text-slate-500 text-xs font-medium">
                                    Securely managed by CloudVault
                                </span>
                            </div>
                            <button className="text-[#8b5cf6] text-xs font-bold hover:underline">
                                Edit Settings
                            </button>
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}

// Sub-component for Info Tiles
function InfoTile({
    icon,
    label,
    value,
}: {
    icon: React.ReactNode;
    label: string;
    value: string;
}) {
    return (
        <div className="p-4 rounded-2xl bg-slate-800/30 border border-slate-800 flex items-start gap-4">
            <div className="p-2.5 bg-slate-900 rounded-xl border border-slate-700">
                {icon}
            </div>
            <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    {label}
                </p>
                <p className="text-slate-200 font-semibold mt-0.5">{value}</p>
            </div>
        </div>
    );
}
